import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  useAddOmnisendEventMutation,
  useGetAssetsQuery,
  useGetIngredientPricesQuery,
  useGetIngredientsQuery,
  useGetProductPricesQuery,
  useCreateCheckoutSessionMutation,
  useUpdateQuizIngredientsMutation,
} from '../features/quizApiSlice';
import { calculateIngredientPrice } from '../utils/productSuggestions';
import useFormulaEditor from './useFormulaEditor';
import { toast } from 'react-toastify';
import { useMetaApiMutation } from '../features/metaApiSlice';
import { v4 as uuidv4 } from 'uuid';
import { getValidFbParam, saveMetaParamsBeforeRedirect } from '../utils';
import { sendOmnisendAddToCartEvent } from '../utils/omnisend';
import { waitForTracking } from '../utils/tracking';
import { getStripeLocale } from '../utils/language';
import { buildReferralClientReference, getCheckoutReferralCode } from '../utils/referral';
import { clearCachedAnswersWebResults } from '../utils/answersWebCache';

const ONE_OFF_SUB = {
  picture: 'resources/images/boxsubs-monthly.jpg',
  duration: '30 days',
  description: 'One time subscription for 30 days',
  title: '30 days',
  id: 999,
};

const THREE_MONTHS_ONE_OFF_SUB = {
  picture: 'resources/images/boxsubs-monthly.jpg',
  duration: '90 days',
  description: 'One time subscription for 90 days',
  title: '90 days',
  id: 9999,
};

const PRELOAD_CHECKOUT_DURATIONS = ['3 months', '6 months'];
const PRELOADED_CHECKOUT_TTL_MS = 20 * 60 * 1000;

const isOneOffCheckout = (duration) => ['30 days', '90 days'].includes(duration);

function amountMatches(dbAmount, targetAmount) {
  const normalizedDbAmount = Number(dbAmount);
  const normalizedTargetAmount = Number(targetAmount);

  if (!Number.isFinite(normalizedDbAmount) || !Number.isFinite(normalizedTargetAmount)) {
    return false;
  }

  return Math.abs(normalizedDbAmount - normalizedTargetAmount) < 0.01;
}

const hasPaymentLink = (price) => Boolean(price?.payment_link);

function getTargetAmountByDuration(duration, totals) {
  if (duration === '6 months') return Math.round(parseFloat(totals?.sixMonths || 0) * 3);
  if (duration === '3 months') return Math.round(parseFloat(totals?.threeMonths || 0));
  if (duration === '1 month' || duration === '30 days') return Math.round(parseFloat(totals?.oneMonth || 0));
  if (duration === '90 days') return Math.round(parseFloat(totals?.threeMonthsOneOff || 0));
  return null;
}

function getFormulaSignature(items = []) {
  return items
    .map((item) => `${item?.ingredient_id || item?.id || ''}:${item?.daily_dose || ''}`)
    .sort()
    .join('|');
}

function getIngredientChangeKey(item) {
  return item?.ingredient_id ?? item?.id ?? '';
}

function buildFormulaChanges(currentItems = [], originalItems = []) {
  const filteredObjects = currentItems.filter((currentItem) => {
    const currentKey = getIngredientChangeKey(currentItem);
    const originalItem = originalItems?.find(
      (item) => String(getIngredientChangeKey(item)) === String(currentKey),
    );
    return originalItem && currentItem.daily_dose !== originalItem.daily_dose;
  });
  const addedItems = [];
  const removedItems = [];

  currentItems?.forEach((item) => {
    const itemKey = getIngredientChangeKey(item);
    const ingFound = originalItems?.find(
      (originalItem) => String(getIngredientChangeKey(originalItem)) === String(itemKey),
    );
    if (!ingFound) {
      addedItems.push(item);
    }
  });

  originalItems?.forEach((oldItem) => {
    const oldItemKey = getIngredientChangeKey(oldItem);
    if (!currentItems?.some((newItem) => String(getIngredientChangeKey(newItem)) === String(oldItemKey))) {
      removedItems.push(oldItem);
    }
  });

  return {
    filteredObjects,
    addedItems,
    removedItems,
  };
}

function buildCheckoutCacheKey({
  duration,
  amount,
  promoCode,
  locale,
  email,
  clientReferenceId,
  quizId,
  cancelUrl,
  formulaSignature,
  productType,
  includeOmega3,
  checkoutContext,
}) {
  return [
    duration || '',
    amount ?? '',
    promoCode || '',
    locale || '',
    email || '',
    clientReferenceId || '',
    quizId || '',
    cancelUrl || '',
    formulaSignature || '',
    productType || '',
    includeOmega3 ? 'omega3' : '',
    checkoutContext || '',
  ].join('::');
}

function isPreloadedCheckoutFresh(entry) {
  return Boolean(entry?.url && Date.now() - entry.createdAt < PRELOADED_CHECKOUT_TTL_MS);
}

const useProduct = ({ autoPreloadCheckoutSessions = true } = {}) => {
  let { state, search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const discountCode = searchParams.get('discountCode') || '';

  const [lang, setLang] = useState();
  const [data, setData] = useState([]);
  const [focusArea, setFocusArea] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [newSubs, setNewSubs] = useState([]);

  const [quizId, setQuizId] = useState(0);
  const [user, setUser] = useState();
  const [hasFormulaChanged, setHasFormulaChanged] = useState(false);
  const [error, setError] = useState({});
  const [prices, setPrices] = useState({});
  const [pricesData, setPricesData] = useState({});
  const [totals, setTotals] = useState({
    oneMonth: 0,
    threeMonths: 0,
    sixMonths: 0,
    threeMonthsOneOff: 0,
  });
  const [continent, setContinent] = useState('eu');
  const [paymentLink, setPaymentLink] = useState(null);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [preloadedCheckoutUrls, setPreloadedCheckoutUrls] = useState({});
  const preloadRequestsRef = useRef({});
  const preloadedFormulaSignatureRef = useRef('');

  // onSubmitData leaves isCheckoutLoading=true while it redirects to Stripe (window.location.href).
  // If the user clicks the browser Back button, the page is restored from the bfcache with React
  // state frozen, so the checkout button would stay disabled. Reset the flag when the page is
  // restored from cache so the button is live again with the already-selected plan.
  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) setIsCheckoutLoading(false);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const [subscribtion, setSubscription] = useState({
    title: '',
    duration: '',
    description: '',
    subId: 1,
    expirey: Date,
  });
  const [addIngredientsModal, setAddIngredientsModal] = useState(false);
  const [token] = useState(localStorage.getItem('customer_token'));

  // All queries and mutations
  const [updateQuizIngredientsQuantity, { isLoading: isUpdateQuizIngredientsQuantityLoading }] =
    useUpdateQuizIngredientsMutation();
  const [addOmnisendEventMutation, { isLoading: isAddOmnisendEventMutationLoading }] =
    useAddOmnisendEventMutation();
  const [createCheckoutSession, { isLoading: isCreateCheckoutSessionLoading }] =
    useCreateCheckoutSessionMutation();

  const { data: allProductPrices, isLoading: isProductPricingLoading } = useGetProductPricesQuery();
  const { data: focusAreaIcons, isLoading: isFocusAreaIconsLoading } = useGetAssetsQuery({
    section: 'focusAreaIcon',
    lang,
  });
  const { data: allIngredients, isLoading: isAllIngredientsLoading } = useGetIngredientsQuery(lang);
  const { data: allIngredientPrices, isLoading: allIngredientPriceLoading } =
    useGetIngredientPricesQuery(lang);

  const handleQuantityPriceChange = useCallback((item, quantity) => {
    calculateIngredientPrice(
      item,
      quantity,
      setPrices,
      setPaymentLink,
      setSubscription,
      allIngredientPrices,
      true,
    );
  }, [allIngredientPrices]);

  const handleFormulaInvalidated = useCallback(() => {
    setPaymentLink(null);
  }, []);

  const {
    addIngredient,
    onAddQuantity,
    onInputChange,
    onRemoveQuantity,
    removeIngredient,
    removeIngredientFromBin,
    resetFormula,
  } = useFormulaEditor({
    setData,
    originalIngredients: ingredients,
    setHasFormulaChanged,
    setError,
    setPrices,
    pricesData,
    allIngredients,
    onQuantityPriceChange: handleQuantityPriceChange,
    onFormulaInvalidated: handleFormulaInvalidated,
  });

  const getCheckoutIdentity = useCallback(
    (duration, checkoutOverrides = {}) => {
      const storedEmail = localStorage.getItem('email');
      const email =
        checkoutOverrides.email !== undefined
          ? checkoutOverrides.email
          : state?.user?.user?.email || user?.email || storedEmail || null;
      const userId = state?.user?.user?.id || user?.id || null;
      const isOneOff = isOneOffCheckout(duration);
      const referralCode = getCheckoutReferralCode();
      let clientReferenceId =
        isOneOff && referralCode ? buildReferralClientReference(userId, referralCode) : String(userId || '');
      if (checkoutOverrides.clientReferenceId !== undefined) {
        clientReferenceId = checkoutOverrides.clientReferenceId;
      }
      const promoCode =
        checkoutOverrides.promoCode !== undefined
          ? checkoutOverrides.promoCode
          : discountCode || (isOneOff ? '' : referralCode);
      const locale =
        checkoutOverrides.locale !== undefined ? checkoutOverrides.locale : getStripeLocale(lang);

      return {
        email,
        clientReferenceId,
        promoCode,
        locale,
      };
    },
    [
      discountCode,
      lang,
      state?.user?.user?.email,
      state?.user?.user?.id,
      user?.email,
      user?.id,
    ],
  );

  const getProductPriceBySelection = useCallback(
    (duration, amount, productType = 'riise_formula') => {
      const productPrices = allProductPrices?.data ?? [];

      return productPrices.find(
        (price) =>
          price?.product_type === productType &&
          price?.duration === duration &&
          amountMatches(price?.amount, amount),
      );
    },
    [allProductPrices?.data],
  );

  const buildSubscriptionCheckoutRequest = useCallback(
    (duration, checkoutOverrides = {}) => {
      const productType = checkoutOverrides.productType || 'riise_formula';
      const selectedAmount =
        checkoutOverrides.selectedAmount ?? getTargetAmountByDuration(duration, totals);
      const productPrice = getProductPriceBySelection(duration, selectedAmount, productType);

      if (!selectedAmount || !productPrice?.id) return null;

      const { email, clientReferenceId, promoCode, locale } = getCheckoutIdentity(
        duration,
        checkoutOverrides,
      );
      const checkoutQuizId = checkoutOverrides.quizId ?? quizId;
      const cancelUrl = checkoutOverrides.cancelUrl || window.location.href;
      const formulaSignature = checkoutOverrides.formulaSignature || getFormulaSignature(data);
      const includeOmega3 = Boolean(checkoutOverrides.includeOmega3);
      const checkoutContext = checkoutOverrides.checkoutContext;
      const cacheKey = buildCheckoutCacheKey({
        duration,
        amount: selectedAmount,
        promoCode,
        locale,
        email,
        clientReferenceId,
        quizId: checkoutQuizId,
        cancelUrl,
        formulaSignature,
        productType,
        includeOmega3,
        checkoutContext,
      });

      return {
        cacheKey,
        payload: {
          productPriceId: productPrice.id,
          email,
          clientReferenceId,
          locale: locale || undefined,
          promoCode: promoCode || undefined,
          quizId: checkoutQuizId,
          productType,
          duration,
          selectedAmount,
          cancelUrl,
          includeOmega3: includeOmega3 || undefined,
          checkoutContext,
        },
      };
    },
    [data, getCheckoutIdentity, getProductPriceBySelection, quizId, totals],
  );

  const onSelectPlan = useCallback((selectedPlan) => {
    const duration = selectedPlan?.duration;

    // Update subscription state immutably (avoid stale UI due to direct mutation)
    setSubscription((prev) => {
      const isSameSelection =
        prev?.subId === selectedPlan?.id &&
        prev?.duration === duration &&
        prev?.title === selectedPlan?.title &&
        prev?.description === selectedPlan?.description;

      if (isSameSelection) return prev;

      const now = Date.now();
      const digit = duration?.match(/\d+/) ? parseInt(duration.match(/\d+/)[0], 10) : 0;
      const expirey =
        digit !== 0 && !isOneOffCheckout(duration)
          ? new Date(now + Number(digit) * 30 * 24 * 60 * 60 * 1000).toLocaleDateString()
          : new Date(now).toLocaleDateString();

      return {
        ...prev,
        title: selectedPlan?.title,
        duration,
        subId: selectedPlan?.id,
        description: selectedPlan?.description,
        expirey,
      };
    });
  }, []);

  useEffect(() => {
    const duration = subscribtion?.duration;
    const productPrices = allProductPrices?.data ?? [];

    if (!duration || !productPrices.length) return;

    const targetAmount = getTargetAmountByDuration(duration, totals);
    if (targetAmount == null) {
      setPaymentLink(null);
      return;
    }

    const price = productPrices.find(
      (item) =>
        hasPaymentLink(item) &&
        item?.product_type === 'riise_formula' &&
        item?.duration === duration &&
        amountMatches(item?.amount, targetAmount),
    );
    const nextPaymentLink = price?.payment_link ?? null;

    setPaymentLink((currentPaymentLink) =>
      currentPaymentLink === nextPaymentLink ? currentPaymentLink : nextPaymentLink,
    );
  }, [allProductPrices?.data, subscribtion?.duration, totals]);

  const preloadCheckoutSessions = useCallback(
    (checkoutOverrides = {}, { force = false } = {}) => {
      const hasValidTotals =
        totals?.oneMonth > 0 ||
        totals?.threeMonths > 0 ||
        totals?.sixMonths > 0 ||
        totals?.threeMonthsOneOff > 0;
      const checkoutQuizId = checkoutOverrides.quizId ?? quizId;

      if (!checkoutQuizId || !data.length || !newSubs.length || !hasValidTotals) {
        return Promise.resolve([]);
      }

      const supportedDurations = checkoutOverrides.checkoutContext === 'product_suggestion' && checkoutOverrides.includeOmega3
        ? [...PRELOAD_CHECKOUT_DURATIONS, '30 days', '90 days']
        : PRELOAD_CHECKOUT_DURATIONS;
      const preloadDurations = supportedDurations.filter((duration) =>
        newSubs.some((sub) => sub?.duration === duration),
      );
      if (!preloadDurations.length) return Promise.resolve([]);

      preloadedFormulaSignatureRef.current = getFormulaSignature(data);

      const preloadPromises = preloadDurations.map((duration) => {
        const checkoutRequest = buildSubscriptionCheckoutRequest(duration, {
          ...checkoutOverrides,
          quizId: checkoutQuizId,
        });
        if (!checkoutRequest) return Promise.resolve(null);

        const { cacheKey, payload } = checkoutRequest;
        if (!force && isPreloadedCheckoutFresh(preloadedCheckoutUrls[cacheKey])) {
          return Promise.resolve(preloadedCheckoutUrls[cacheKey].url);
        }
        if (preloadRequestsRef.current[cacheKey]) {
          return preloadRequestsRef.current[cacheKey];
        }

        preloadRequestsRef.current[cacheKey] = createCheckoutSession(payload)
          .unwrap()
          .then((checkoutSession) => {
            const checkoutUrl = checkoutSession?.data?.url;
            if (!checkoutUrl) return null;

            setPreloadedCheckoutUrls((currentUrls) => ({
              ...currentUrls,
              [cacheKey]: {
                url: checkoutUrl,
                createdAt: Date.now(),
              },
            }));

            return checkoutUrl;
          })
          .catch((err) => {
            return null;
          })
          .finally(() => {
            delete preloadRequestsRef.current[cacheKey];
          });

        return preloadRequestsRef.current[cacheKey];
      });

      return Promise.allSettled(preloadPromises);
    },
    [
      buildSubscriptionCheckoutRequest,
      createCheckoutSession,
      data,
      newSubs,
      preloadedCheckoutUrls,
      quizId,
      totals,
    ],
  );

  useEffect(() => {
    if (!autoPreloadCheckoutSessions) return;

    const currentFormulaSignature = getFormulaSignature(data);
    const isEditingUnpreloadedFormula =
      hasFormulaChanged && currentFormulaSignature !== preloadedFormulaSignatureRef.current;

    if (isEditingUnpreloadedFormula) return;

    preloadCheckoutSessions();
  }, [autoPreloadCheckoutSessions, data, hasFormulaChanged, preloadCheckoutSessions]);

  const [metaApi] = useMetaApiMutation();

  const getMetaEventValue = (sub) => {
    return getTargetAmountByDuration(sub?.duration, totals) || 0;
  };

  const trackBrowserMetaEvent = (event_name, value, id) => {
    if (!window.fbq) return;

    window.fbq(
      'track',
      event_name,
      {
        value,
        currency: 'EUR',
      },
      {
        eventID: id,
      },
    );
  };

  const metaEvents = async (sub, eventNames) => {
    try {
      const email = localStorage.getItem('email');
      const external_id = sessionStorage.getItem('externalId');
      const fbp = getValidFbParam(window?.clientParamBuilder?.getFbp());
      const fbc = getValidFbParam(window?.clientParamBuilder?.getFbc());
      const event_source_url = window.location.origin + window.location.pathname;
      const value = getMetaEventValue(sub);
      const events = eventNames.map((event_name) => {
        const id = uuidv4();
        trackBrowserMetaEvent(event_name, value, id);

        return {
          event_name,
          value,
          currency: 'EUR',
          page: window.location.href,
          email,
          id,
          external_id,
          fbp,
          fbc,
          event_source_url,
        };
      });

      return metaApi({ events })
        .unwrap()
        .catch((error) => {
          console.log('meta error =>', error);
        });
    } catch (error) {
      console.log('meta error =>', error);
    }
  };
  const getFormulaChanges = useCallback(() => buildFormulaChanges(data, ingredients), [data, ingredients]);

  const buildPaymentLinkCheckoutUrl = useCallback(
    ({ payLink, email, clientReferenceId, promoCode, locale }) => {
      if (!payLink) return null;

      const checkoutParams = new URLSearchParams({
        prefilled_email: email || '',
        client_reference_id: clientReferenceId,
      });

      if (promoCode) {
        checkoutParams.set('prefilled_promo_code', promoCode);
      }
      if (locale) checkoutParams.set('locale', locale);

      return `${payLink}?${checkoutParams.toString()}`;
    },
    [],
  );

  const createServerCheckoutUrl = useCallback(
    async (checkoutRequest) => {
      if (!checkoutRequest) {
        throw new Error('Checkout price not found.');
      }

      const cachedCheckout = preloadedCheckoutUrls[checkoutRequest.cacheKey];
      if (isPreloadedCheckoutFresh(cachedCheckout)) {
        return cachedCheckout.url;
      }

      const pendingCheckoutUrl = preloadRequestsRef.current[checkoutRequest.cacheKey]
        ? await preloadRequestsRef.current[checkoutRequest.cacheKey]
        : null;
      if (pendingCheckoutUrl) {
        return pendingCheckoutUrl;
      }

      const checkoutSession = await createCheckoutSession(checkoutRequest.payload).unwrap();
      const checkoutUrl = checkoutSession?.data?.url;

      if (!checkoutUrl) {
        throw new Error('Checkout session could not be created.');
      }

      setPreloadedCheckoutUrls((currentUrls) => ({
        ...currentUrls,
        [checkoutRequest.cacheKey]: {
          url: checkoutUrl,
          createdAt: Date.now(),
        },
      }));

      return checkoutUrl;
    },
    [createCheckoutSession, preloadedCheckoutUrls],
  );

  const resolveSubscriptionCheckoutUrl = useCallback(
    (duration, checkoutOverrides = {}) => {
      const checkoutRequest = buildSubscriptionCheckoutRequest(duration, checkoutOverrides);
      return createServerCheckoutUrl(checkoutRequest);
    },
    [buildSubscriptionCheckoutRequest, createServerCheckoutUrl],
  );

  const resolveCheckoutUrl = useCallback(
    ({ duration, checkoutOverrides = {}, paymentLinkCheckoutUrl, shouldUseServerCheckout }) =>
      shouldUseServerCheckout
        ? resolveSubscriptionCheckoutUrl(duration, checkoutOverrides)
        : Promise.resolve(paymentLinkCheckoutUrl),
    [resolveSubscriptionCheckoutUrl],
  );

  const waitForCheckoutTracking = ({ checkoutUrl, email }) =>
    waitForTracking([
      metaEvents(subscribtion, ['AddToCart', 'InitiateCheckout']),
      sendOmnisendAddToCartEvent({
        addOmnisendEventMutation,
        email,
        url: checkoutUrl,
        subscribtion,
        totals,
      }),
    ]);

  const redirectToCheckout = async ({ checkoutUrlPromise, email }) => {
    const checkoutUrl = await checkoutUrlPromise;
    await waitForCheckoutTracking({ checkoutUrl, email });
    saveMetaParamsBeforeRedirect();
    window.location.href = checkoutUrl;
  };

  const onSubmitData = async (payLink, checkoutOverrides = {}) => {
    const isOneOff = isOneOffCheckout(subscribtion?.duration);
    if (isCheckoutLoading || (isOneOff && !payLink && !checkoutOverrides.includeOmega3)) return;

    setIsCheckoutLoading(true);
    const {
      email,
      clientReferenceId,
      promoCode: checkoutPromoCode,
      locale: stripeLocale,
    } = getCheckoutIdentity(subscribtion?.duration);
    const paymentLinkCheckoutUrl = isOneOff
      ? buildPaymentLinkCheckoutUrl({
          payLink,
          email,
          clientReferenceId,
          promoCode: checkoutPromoCode,
          locale: stripeLocale,
        })
      : null;
    const shouldUseServerCheckout = !isOneOff || Boolean(checkoutOverrides.includeOmega3);

    if (hasFormulaChanged) {
      try {
        const { filteredObjects, addedItems, removedItems } = getFormulaChanges();
        const shouldSaveIngredients =
          filteredObjects.length > 0 || addedItems.length > 0 || removedItems.length > 0;
        const checkoutUrlPromise = resolveCheckoutUrl({
          shouldUseServerCheckout,
          checkoutOverrides,
          duration: subscribtion?.duration,
          paymentLinkCheckoutUrl,
        });

        if (!shouldSaveIngredients) {
          setHasFormulaChanged(false);
          await redirectToCheckout({ checkoutUrlPromise, email });
          return;
        }

        const ingredientSavePromise = updateQuizIngredientsQuantity({
          updatedItems: filteredObjects,
          addedItems,
          removedItems,
          quizId,
        }).unwrap();
        const checkoutUrl = await checkoutUrlPromise;

        await Promise.all([ingredientSavePromise, waitForCheckoutTracking({ checkoutUrl, email })]);
        // The formula on the server is no longer what the cached /quiz/answersweb
        // result says. Without this the customer returns from Stripe within the
        // cache's 10-minute TTL and the page renders the pre-edit snapshot without
        // ever calling the API — their dose change or added ingredient looks lost,
        // and a hard refresh does not help because sessionStorage survives reloads.
        clearCachedAnswersWebResults();
        setIngredients(data);
        setHasFormulaChanged(false);
        saveMetaParamsBeforeRedirect();
        window.location.href = checkoutUrl;
      } catch (err) {
        setIsCheckoutLoading(false);
        toast.error(err?.data?.message || err?.message || 'Something went wrong');
        console.log('err => ', err);
      }
    } else {
      try {
        const checkoutUrlPromise = resolveCheckoutUrl({
          shouldUseServerCheckout,
          checkoutOverrides,
          duration: subscribtion?.duration,
          paymentLinkCheckoutUrl,
        });
        await redirectToCheckout({ checkoutUrlPromise, email });
      } catch (err) {
        setIsCheckoutLoading(false);
        toast.error(err?.data?.message || err?.message || 'Something went wrong');
        console.log('err => ', err);
      }
    }
  };
  const onSelectOneOff = (selectedPlan) => {
    if (selectedPlan.duration === '30 days') {
      const price = allProductPrices?.data?.find(
        (price) =>
          hasPaymentLink(price) &&
          price.product_type === 'riise_formula' &&
          price.duration === '30 days' &&
          amountMatches(price.amount, Math.round(parseFloat(totals['oneMonth']))),
      );
      setPaymentLink(price?.payment_link);
      onSubmitData(price?.payment_link);
    }
  };

  const getTargetAmount = (duration) => {
    return getTargetAmountByDuration(duration, totals);
  };

  return {
    onInputChange,
    removeIngredientFromBin,
    addIngredient,
    removeIngredient,
    onSubmitData,
    onRemoveQuantity,
    onAddQuantity,
    onSelectPlan,
    preloadCheckoutSessions,
    preloadSubscriptionCheckouts: preloadCheckoutSessions,
    buildPaymentLinkCheckoutUrl,
    buildSubscriptionCheckoutRequest,
    resolveCheckoutUrl,
    resolveSubscriptionCheckoutUrl,
    getFormulaChanges,
    focusAreaIcons,
    allIngredients,
    allIngredientPrices,
    allIngredientPriceLoading,
    allProductPrices,
    isProductPricingLoading,
    updateQuizIngredientsQuantity,
    onSelectOneOff,
    resetFormula,
    //states
    lang,
    setLang,
    data,
    setData,
    focusArea,
    setFocusArea,
    ingredients,
    setIngredients,
    quizId,
    setQuizId,
    user,
    setUser,
    hasFormulaChanged,
    setHasFormulaChanged,
    error,
    setError,
    prices,
    setPrices,
    pricesData,
    setPricesData,
    totals,
    setTotals,
    continent,
    setContinent,
    paymentLink,
    setPaymentLink,
    isCheckoutLoading,
    subscribtion,
    setSubscription,
    token,
    addIngredientsModal,
    setAddIngredientsModal,
    newSubs,
    setNewSubs,
    ONE_OFF_SUB,
    THREE_MONTHS_ONE_OFF_SUB,
    isUpdateQuizIngredientsQuantityLoading,
    isAddOmnisendEventMutationLoading,
    isCreateCheckoutSessionLoading,
    isFocusAreaIconsLoading,
    isAllIngredientsLoading,
    getTargetAmount,
    amountMatches,
    hasPaymentLink,
    isOneOffCheckout,
  };
};

export default useProduct;
